import { CallInitiator, CallStatus, ClientCall } from '@numeo/types'
import { ClientLoadModel } from '@numeo/types-core'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export enum CallType {
    AI = 'AI',
}

export interface ActiveAvailableCall {
    load: ClientLoadModel
    call?: ClientCall
    initiatedBy: CallInitiator
    callId: string
    phoneNumber: string
}
export interface CallCenterState {
    applicationActiveCalls: ClientCall[]
    aiHubActiveCalls: ClientCall[]
    defaultCallType: CallType

    activeAvailableCalls: Record<string, ActiveAvailableCall>
}

const initialState: CallCenterState = {
    applicationActiveCalls: [],
    aiHubActiveCalls: [],
    activeAvailableCalls: {},
    defaultCallType: CallType.AI,
}

const callCenterSlice = createSlice({
    name: 'callCenter',
    initialState,
    reducers: {
        setApplicationActiveCalls(state, action: PayloadAction<ClientCall[]>) {
            state.applicationActiveCalls = action.payload
        },

        setAllCallTypePreferences(state, action: PayloadAction<CallType>) {
            state.defaultCallType = action.payload
        },

        upsertActiveAvailableCall(
            state,
            action: PayloadAction<{
                load?: ClientLoadModel
                call?: ClientCall
                phoneNumber?: string
                initiatedBy?: CallInitiator
                callId: string
            }>
        ) {
            const currentCall = state.activeAvailableCalls[action.payload.callId]
            if (!currentCall && !action.payload.initiatedBy) {
                return
            }

            state.activeAvailableCalls[action.payload.callId] = {
                ...(currentCall || {}),
                ...action.payload,
            }
        },
        removeCall(state, action: PayloadAction<string>) {
            const callId = action.payload
            delete state.activeAvailableCalls[callId]
            const removingCallIndex = state.applicationActiveCalls.findIndex((call) => call.callId === callId)
            if (removingCallIndex !== -1) {
                state.applicationActiveCalls.splice(removingCallIndex, 1)
            }
        },
        endApplicationActiveCall(state, action: PayloadAction<string>) {
            const callId = action.payload
            delete state.activeAvailableCalls[callId]
            const call = state.applicationActiveCalls.find((c) => c.callId === callId)
            if (call) {
                call.status = CallStatus.ENDED
            }
        },
        addApplicationActiveCall(state, action: PayloadAction<ClientCall>) {
            const existing = state.applicationActiveCalls.find((c) => c.callId === action.payload.callId)
            if (!existing) {
                state.applicationActiveCalls.push(action.payload)
            }
        },
        updateApplicationActiveCall(state, action: PayloadAction<ClientCall>) {
            const index = state.applicationActiveCalls.findIndex((c) => c.callId === action.payload.callId)
            if (index !== -1) {
                state.applicationActiveCalls[index] = action.payload
            }
        },
        setAiHubActiveCalls(state, action: PayloadAction<ClientCall[]>) {
            state.aiHubActiveCalls = action.payload
        },
        addAiHubActiveCall(state, action: PayloadAction<ClientCall>) {
            const existing = state.aiHubActiveCalls.find((c) => c.callId === action.payload.callId)
            if (!existing) {
                state.aiHubActiveCalls.push(action.payload)
            }
        },
        updateAiHubActiveCall(state, action: PayloadAction<ClientCall>) {
            const index = state.aiHubActiveCalls.findIndex((c) => c.callId === action.payload.callId)
            if (index !== -1) {
                state.aiHubActiveCalls[index] = action.payload
            }
        },
        endAiHubActiveCall(state, action: PayloadAction<string>) {
            const call = state.aiHubActiveCalls.find((c) => c.callId === action.payload)
            if (call) {
                call.status = CallStatus.ENDED
            }
        },
    },
})

export const {
    // Existing WebSocket system
    removeCall,
    setApplicationActiveCalls,
    addApplicationActiveCall,
    updateApplicationActiveCall,
    endApplicationActiveCall,

    setAiHubActiveCalls,
    addAiHubActiveCall,
    updateAiHubActiveCall,
    endAiHubActiveCall,

    // ClientCall type preferences
    setAllCallTypePreferences,

    upsertActiveAvailableCall,
} = callCenterSlice.actions

export default callCenterSlice.reducer
